"use client"

import { useEffect, useState, useCallback, useRef } from "react"
import { useDebounce } from "use-debounce"

interface PriceFacetsProps {
  initMin: number | null
  initMax: number | null
  labels: {
    minPrice: string
    maxPrice: string
    minPlaceholder: string
    maxPlaceholder: string
  }
  setFacets: (value: { minPrice: number | null; maxPrice: number | null }) => void
  disabled?: boolean
}

export function PriceFacets({
  initMin,
  initMax,
  labels,
  setFacets,
  disabled = false
}: PriceFacetsProps) {
  // Use string state to prevent NaN issues during input
  const [minInput, setMinInput] = useState<string>("")
  const [maxInput, setMaxInput] = useState<string>("")
  const initialRender = useRef(true)

  // Safe number conversion with validation
  const getNumericValue = useCallback((value: string): number | null => {
    if (!value.trim()) return null
    const num = Number(value.replace(/,/g, '.')) // Handle both decimal separators
    return !isNaN(num) && num >= 0 ? num : null
  }, [])

  // Debounced values for parent updates
  const [debouncedMinPrice] = useDebounce(getNumericValue(minInput), 500)
  const [debouncedMaxPrice] = useDebounce(getNumericValue(maxInput), 500)

  // Initialize state safely after mount
  useEffect(() => {
    if (initialRender.current) {
      setMinInput(initMin?.toString() ?? "")
      setMaxInput(initMax?.toString() ?? "")
      initialRender.current = false
    }
  }, [initMin, initMax])

  // Sync with parent only when values change
  useEffect(() => {
    if (initialRender.current) return

    const min = debouncedMinPrice
    const max = debouncedMaxPrice

    // Validate min <= max before updating
    if (min !== null && max !== null && min > max) {
      // Auto-correct by swapping values
      setMinInput(max.toString())
      setMaxInput(min.toString())
      return
    }

    try {
      setFacets({
        minPrice: min,
        maxPrice: max
      })
    } catch (error) {
      console.error("Price filter update failed:", error)
      // Revert to last valid values
      setMinInput(initMin?.toString() ?? "")
      setMaxInput(initMax?.toString() ?? "")
    }
  }, [debouncedMinPrice, debouncedMaxPrice, setFacets, initMin, initMax])

  // Handle input changes with validation
  const handleInputChange = (type: 'min' | 'max', value: string) => {
    // Allow empty string or properly formatted numbers
    if (value === "" || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
      const sanitizedValue = value.replace(/,/g, '.') // Normalize decimal separator
      type === 'min' ? setMinInput(sanitizedValue) : setMaxInput(sanitizedValue)
    }
  }

  // Format displayed value with thousands separators
  const formatDisplayValue = (value: string) => {
    if (!value) return value
    const parts = value.split('.')
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    return parts.join('.')
  }

  return (
    <div className="space-y-3" role="group" aria-labelledby="price-filter-label">
      <span id="price-filter-label" className="sr-only">Price range filter</span>

      <div className="flex items-center gap-2">
        <label htmlFor="minPrice" className="text-sm min-w-[80px] text-gray-500">
          {labels.minPrice}
        </label>
        <input
          id="minPrice"
          type="text"
          inputMode="decimal"
          value={formatDisplayValue(minInput)}
          placeholder={labels.minPlaceholder}
          onChange={(e) => handleInputChange('min', e.target.value)}
          onBlur={() => setMinInput(minInput.replace(/,/g, ''))}
          className="w-full rounded border px-2 py-1 text-sm disabled:opacity-50"
          aria-label={`Minimum ${labels.minPrice}`}
          disabled={disabled}
          aria-disabled={disabled}
        />
      </div>

      <div className="flex items-center gap-2">
        <label htmlFor="maxPrice" className="text-sm min-w-[80px] text-gray-500">
          {labels.maxPrice}
        </label>
        <input
          id="maxPrice"
          type="text"
          inputMode="decimal"
          value={formatDisplayValue(maxInput)}
          placeholder={labels.maxPlaceholder}
          onChange={(e) => handleInputChange('max', e.target.value)}
          onBlur={() => setMaxInput(maxInput.replace(/,/g, ''))}
          className="w-full rounded border px-2 py-1 text-sm disabled:opacity-50"
          aria-label={`Maximum ${labels.maxPrice}`}
          disabled={disabled}
          aria-disabled={disabled}
        />
      </div>
    </div>
  )
}